/**
 * Tickets Page
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { formatDateTime } from '@/lib/utils';
import { Plus, Search, Ticket, MessageSquare } from 'lucide-react';

interface TicketData {
  id: string;
  ticketNumber: number;
  accountName: string;
  subject: string;
  status: string;
  priority: string;
  assigneeName: string | null;
  applicationName: string | null;
  tenantSlug: string | null;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

interface AppStatusItem {
  applicationId: string;
  applicationName: string;
}

const statusColors: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  waiting_customer: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};

const priorityColors: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600',
};

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_customer: 'Waiting on Customer',
  resolved: 'Resolved',
  closed: 'Closed',
};

export function TicketsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [applicationId, setApplicationId] = useState<string | undefined>();
  const [tenantSlug, setTenantSlug] = useState('');
  const [page, setPage] = useState(1);

  // App list for the filter dropdown (one widget app per embedding application).
  const { data: appData } = useQuery({
    queryKey: ['app-statuses'],
    queryFn: () => api.getAppStatuses(),
  });
  const applications = (appData?.data || []) as AppStatusItem[];

  const trimmedTenant = tenantSlug.trim();
  const { data, isLoading } = useQuery({
    queryKey: ['tickets', { page, search, status: statusFilter, applicationId, tenant: trimmedTenant }],
    queryFn: async () => {
      const result = await api.getTickets({
        page,
        limit: 20,
        search: search || undefined,
        status: statusFilter,
        applicationId: applicationId || undefined,
        tenantSlug: trimmedTenant || undefined,
      });
      return result;
    },
  });

  const tickets = (data?.data || []) as TicketData[];
  const pagination = data?.pagination;
  const hasFilters = Boolean(search || statusFilter || applicationId || trimmedTenant);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Support Tickets</h1>
          <p className="text-muted-foreground">Manage customer support requests</p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Ticket
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tickets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex gap-2">
          <Button
            variant={!statusFilter ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(undefined)}
          >
            All
          </Button>
          <Button
            variant={statusFilter === 'open' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('open')}
          >
            Open
          </Button>
          <Button
            variant={statusFilter === 'in_progress' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('in_progress')}
          >
            In Progress
          </Button>
          <Button
            variant={statusFilter === 'resolved' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('resolved')}
          >
            Resolved
          </Button>
        </div>

        {/* Multi-tenant filters: scope to one embedding app and/or sub-tenant */}
        <select
          value={applicationId ?? ''}
          onChange={(e) => {
            setApplicationId(e.target.value || undefined);
            setPage(1);
          }}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          aria-label="Filter by application"
        >
          <option value="">All applications</option>
          {applications.map((app) => (
            <option key={app.applicationId} value={app.applicationId}>
              {app.applicationName}
            </option>
          ))}
        </select>

        <Input
          placeholder="Tenant (e.g. acme)"
          value={tenantSlug}
          onChange={(e) => {
            setTenantSlug(e.target.value);
            setPage(1);
          }}
          className="max-w-[200px]"
        />
      </div>

      {/* Tickets List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Ticket className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">No tickets found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {hasFilters
                ? 'Try adjusting your search or filters'
                : 'No support tickets have been created yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {tickets.map((ticket) => (
            <Card
              key={ticket.id}
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => navigate(ticket.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm text-muted-foreground">#{ticket.ticketNumber}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[ticket.status]}`}>
                        {statusLabels[ticket.status] || ticket.status}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${priorityColors[ticket.priority]}`}>
                        {ticket.priority}
                      </span>
                      {ticket.applicationName && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                          {ticket.applicationName}
                        </span>
                      )}
                      {ticket.tenantSlug && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
                          {ticket.tenantSlug}
                        </span>
                      )}
                    </div>
                    <h3 className="font-medium truncate">{ticket.subject}</h3>
                    <p className="text-sm text-muted-foreground">
                      {ticket.accountName} • {ticket.assigneeName || 'Unassigned'}
                    </p>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="flex items-center gap-1 text-muted-foreground mb-1">
                      <MessageSquare className="h-4 w-4" />
                      <span className="text-sm">{ticket.messageCount}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(ticket.updatedAt)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
